import { z } from 'zod';
import { ObjectIdSchema } from '../../shared/object-id.schema';
/**
 * Position of a published/draft step keyed by its `stableStepId`.
 * `stableStepId` matches `ProcessDraftStep.stableStepId` in the draft scope
 * and `ProcessStepRecording._id` in the published scope — the same id across
 * both, so layout survives the publish round-trip without rewriting.
 */ export const StepPositionSchema = z.object({
    stableStepId: ObjectIdSchema,
    x: z.number(),
    y: z.number()
});
/**
 * Position of a synthetic node (not a step): `start-node`, `end-<stableStepId>`
 * (optionally suffixed with `-<branchKey>`), or `gateway-<stableStepId>`.
 * Key shape matches the React Flow node ids emitted by the flow builder.
 */ export const GeneratedNodePositionSchema = z.object({
    nodeId: z.string().min(1),
    x: z.number(),
    y: z.number()
});
/**
 * A user-placed bend in an edge. Waypoints are ordered from source to target;
 * the renderer walks source → wp₁ → wp₂ → … → target as an orthogonal
 * polyline with rounded corners. Coordinates live in flow-space so the path
 * re-renders correctly under zoom/pan and when endpoint nodes move.
 */ export const EdgeWaypointSchema = z.object({
    id: z.string().min(1),
    x: z.number(),
    y: z.number()
});
/**
 * Layout for a single edge, keyed by the stable `(source, target, handle?)`
 * triple already used for `generatedNodePositions` and `stepPositions`. This
 * identity survives draft→publish because both scopes share the same node
 * ids (real steps use `stableStepId`; synthetic nodes use deterministic
 * strings like `start-node` / `end-<id>` / `gateway-<id>`).
 */ export const EdgeLayoutSchema = z.object({
    sourceNodeId: z.string().min(1),
    targetNodeId: z.string().min(1),
    sourceHandleId: z.string().nullable().optional(),
    waypoints: z.array(EdgeWaypointSchema).default([])
});
/**
 * User-picked base lane color keyed by the derived lane key
 * (`dept-<id>` / `team-<id>` / `system-<id>` / `external-<id>` /
 * `unassigned`). Entries whose lane no longer exists are silently ignored by
 * the renderer — same staleness policy as `swimlaneOrder`.
 */ export const SWIMLANE_COLOR_HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;
export const SwimlaneColorSchema = z.object({
    laneKey: z.string().min(1).max(100),
    color: z.string().regex(SWIMLANE_COLOR_HEX_PATTERN)
});
export const ProcessLayoutScopeSchema = z.enum([
    'draft',
    'published'
]);
/**
 * One layout doc per `{process, scope}` pair. The draft scope holds edits in
 * progress; publishing atomically copies the draft-scoped doc onto the
 * published scope and discards the draft. Discarding a draft drops only the
 * draft-scoped doc.
 */ export const ProcessLayoutSchema = z.object({
    _id: ObjectIdSchema,
    process: ObjectIdSchema,
    scope: ProcessLayoutScopeSchema,
    stepPositions: z.array(StepPositionSchema).default([]),
    generatedNodePositions: z.array(GeneratedNodePositionSchema).default([]),
    swimlaneOrder: z.array(z.string().max(100)).default([]),
    laneColors: z.array(SwimlaneColorSchema).default([]),
    edgeLayouts: z.array(EdgeLayoutSchema).default([]),
    updatedAt: z.date()
});

//# sourceMappingURL=process-layout.schema.js.map